import React, { Component } from "react";
import { get as _get, isEqual as _isEqual, map as _map } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import Helmet from "react-helmet";

import { showAlertMessage, Select } from "../../resusableComponents/common/formElements";
import { hasAccess } from "../../resusableComponents/hoc/AccessManagement";
import { withRouter } from "../../resusableComponents/hoc/withRouter";
import PowerBIDashboard from "../../resusableComponents/common/PowerBIDashboard";

import { fetchClientPowerBIToken } from "../../redux/services/clientDashboard";
import { doGetAgencyList, doGetClientList } from "../../redux/services/userMangement";
import { updatePageLevelLoadingState } from "../../redux/actions/application";

class ClientDashboard extends Component {

  constructor(props) {
    super(props);

    this.state = {
      agencyList: [],
      clientList: [],
      selectedAgency: null,
      selectedClient: null,
      powerBI: {},
      isPowerBIFetched: false,
      activeRequestCount: 0,
    };
  }

  componentDidMount() {
    if (hasAccess("analysis", "client-dashboard", ["agency-filter"])) {
      this._fetchAgencyList();
    }
    if (hasAccess("analysis", "client-dashboard", ["client-filter"])) {
      this._fetchClientList(null);
    }
    this._fetchPowerBIDashboardData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_isEqual(prevState.selectedAgency, this.state.selectedAgency) && hasAccess("analysis", "client-dashboard", ["client-filter"])) {
      const agencyId = _get(this.state.selectedAgency, "id", null);
      this._fetchClientList(agencyId);
    }
  }

  _incrementLoader = () => {
    const { updatePageLevelLoadingState } = this.props;
    this.setState((prevState) => {
      const nextCount = (_get(prevState, "activeRequestCount", 0) + 1);
      if (nextCount === 1 && typeof updatePageLevelLoadingState === "function") {
        updatePageLevelLoadingState(true);
      }
      return { activeRequestCount: nextCount };
    });
  }

  _decrementLoader = () => {
    const { updatePageLevelLoadingState } = this.props;
    this.setState((prevState) => {
      const nextCount = Math.max(0, _get(prevState, "activeRequestCount", 0) - 1);
      if (nextCount === 0 && typeof updatePageLevelLoadingState === "function") {
        updatePageLevelLoadingState(false);
      }
      return { activeRequestCount: nextCount };
    });
  }

  _fetchAgencyList = async () => {
    const { intl } = this.props;
    try {
      this._incrementLoader();
      const response = await doGetAgencyList({ dropdown_flag: 1 });

      if ((_get(response, "flag", false)) === true) {
        this.setState({ agencyList: _get(response, "data.responseData.agency_list", []) });
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_agency", defaultMessage: "Something went wrong while fetching agency list." })));
        this.setState({ agencyList: [] });
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_agency", defaultMessage: "Something went wrong while fetching agency list." })));
    } finally {
      this._decrementLoader();
    }
  }

  _fetchClientList = async (agencyId = null) => {
    const { intl } = this.props;
    try {
      this._incrementLoader();
      const response = await doGetClientList({ agency_id: agencyId, dropdown_flag: 1 });

      if ((_get(response, "flag", false)) === true) {
        this.setState({ clientList: _get(response, "data.responseData.client_list", []) });
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_client", defaultMessage: "Something went wrong while fetching client list." })));
        this.setState({ clientList: [] });
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_client", defaultMessage: "Something went wrong while fetching client list." })));
    } finally {
      this._decrementLoader();
    }
  }

  _fetchPowerBIDashboardData = async (agencyObj = this.state.selectedAgency, clientObj = this.state.selectedClient) => {
    const { intl } = this.props;

    let clientIdPayload = [];
    if (clientObj) {
      clientIdPayload = Array.isArray(clientObj) ? _map(clientObj, "id") : [clientObj.id];
    }

    const agencyIdPayload = agencyObj ? agencyObj.id : 0;

    const payload = {
      client_id: clientIdPayload,
      agency_id: agencyIdPayload
    };

    try {
      this._incrementLoader();
      const response = await fetchClientPowerBIToken("client_dashboard", payload);

      if ((_get(response, "flag", false)) === true) {
        this.setState({ powerBI: _get(response, "data", {}), isPowerBIFetched: true });
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_powerbi", defaultMessage: "Something went wrong while fetching client dashboard data." })));
        this.setState({ powerBI: {}, isPowerBIFetched: false });
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", intl.formatMessage({ id: "client_dashboard.error.fetch_powerbi", defaultMessage: "Something went wrong while fetching client dashboard data." })));
    } finally {
      this._decrementLoader();
    }
  }

  _handleAgencyChange = (selected) => {
    this.setState({ selectedAgency: selected, selectedClient: null });
    this._fetchPowerBIDashboardData(selected, null);
  }

  _handleClientChange = (selected) => {
    this.setState({ selectedClient: selected });
    this._fetchPowerBIDashboardData(this.state.selectedAgency, selected);
  }

  _handleReset = () => {
    this.setState({ selectedAgency: null, selectedClient: null });
    if (hasAccess("analysis", "client-dashboard", ["client-filter"])) {
      this._fetchClientList(null);
    }
    this._fetchPowerBIDashboardData(null, null);
  }

  _renderFilters = () => {
    const { intl } = this.props;
    const { agencyList, clientList, selectedAgency, selectedClient } = this.state;

    const hasAgencyFilterAccess = hasAccess("analysis", "client-dashboard", ["agency-filter"]);
    const hasClientFilterAccess = hasAccess("analysis", "client-dashboard", ["client-filter"]);

    if (!hasAgencyFilterAccess && !hasClientFilterAccess) {
      return null;
    }

    return (
      <div className="row align-items-center mb-4">
        {hasAgencyFilterAccess && (
          <div className="col-lg-3">
            <Select
              className="form-custom-select"
              placeholder={intl.formatMessage({ id: "client_dashboard.placeholder.select_agency", defaultMessage: "Select Agency" })}
              options={(agencyList || [])}
              value={(selectedAgency || null)}
              getOptionLabel={(option) => (option.agency_name)}
              getOptionValue={(option) => (option.id)}
              onChange={this._handleAgencyChange}
              isClearable={true}
              isMulti={false}
            />
          </div>
        )}
        {hasClientFilterAccess && (
          <div className="col-lg-3">
            <Select
              className="form-custom-select"
              placeholder={intl.formatMessage({ id: "client_dashboard.placeholder.select_client", defaultMessage: "Select Client" })}
              options={(clientList || [])}
              value={(selectedClient || null)}
              getOptionLabel={(option) => (option.client_name)}
              getOptionValue={(option) => (option.id)}
              onChange={this._handleClientChange}
              isClearable={true}
              isDisabled={!selectedAgency}
            />
          </div>
        )}
        <div className="col-lg-auto ms-auto">
          <button className="input-group-text btn btn-dark text-capitalize" onClick={this._handleReset}>
            <FormattedMessage id="btn.reset" defaultMessage="reset" />
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { intl } = this.props;
    const { powerBI } = this.state;

    const hasPageViewPermission = hasAccess("analysis", "client-dashboard", ["page-view"]);

    if (!hasPageViewPermission) {
      return null;
    }

    return (
      <>
        <Helmet>
          <title>{intl.formatMessage({ id: "hermes.client_dashboard", defaultMessage: "Client Dashboard: Hermes" })}</title>
        </Helmet>

        <div className="container-fluid flex-grow-1 px-6 py-6">
          <div className="row mb-4">
            <div className="col-lg-12">
              <h1 className="heading-04 fw-semibold text-dark-35 text-uppercase">
                <FormattedMessage id="client_dashboard.title" defaultMessage="client dashboard" />
              </h1>
            </div>
          </div>

          {this._renderFilters()}

          <div className="row">
            <div className="col-lg-12">
              <div className="powerbi-section">
                <PowerBIDashboard
                  reportId={_get(powerBI, "report_id", "")}
                  groupId={_get(powerBI, "workspace_id", "")}
                  embedUrl={_get(powerBI, "app_url", "")}
                  filter={_get(powerBI, "filter", "")}
                  accessToken={_get(powerBI, "access_token", "")}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state, props) => ({});

const mapDispatchToProps = (dispatch) => ({
  updatePageLevelLoadingState: (payload) => dispatch(updatePageLevelLoadingState(payload)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ClientDashboard)));
